import { Link } from "react-router-dom";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { SeoHead } from "@/components/seo/SeoHead";
import { StructuredData } from "@/components/seo/StructuredData";
import {
  getBaseUrl,
  buildBreadcrumbSchema,
  buildOrganizationSchema,
  buildSoftwareApplicationSchema,
  buildWebPageSchema,
  buildFAQPageSchema,
} from "@/lib/seo";
import { Button } from "@/components/ui/button";
import { Instagram, Bot, MessageCircle, ArrowRight } from "lucide-react";

const TITLE = "Agente de IA para Instagram para Direct e comentários | FabChat";
const DESCRIPTION =
  "Agente de IA para Instagram com respostas em Direct e comentários: automação com contexto, regras configuráveis e CRM integrado para leads.";

const BREADCRUMB = [
  { name: "Início", path: "/" },
  { name: "Agente de IA para Instagram", path: "/instagram/agente-ia" },
];

const FAQ_ITEMS = [
  {
    question: "O que é um agente de IA para Instagram?",
    answer:
      "É um assistente de inteligência artificial conectado ao seu perfil do Instagram, que responde a Direct (DM) e comentários com contexto, tom de voz da marca e regras definidas pela empresa. Diferente de respostas automáticas fixas, o agente entende a intenção do seguidor e adapta a conversa — é o novo método para construir relacionamento com o cliente nas redes sociais em escala.",
  },
  {
    question: "Como funciona a automação de DM com agente de IA?",
    answer:
      "Você conecta o Instagram à FabChat, configura a personalidade do agente, as regras e a base de conhecimento. Quando alguém envia uma DM ou comenta um post, o agente responde no mesmo tom da marca, qualifica o lead e encaminha para um humano quando necessário — tudo no mesmo painel do WhatsApp.",
  },
  {
    question: "O agente de IA responde comentários no Instagram?",
    answer:
      "Sim. Além das DMs, o agente também acompanha e responde comentários em posts, dentro das regras configuradas. Isso ajuda a manter o engajamento alto e o relacionamento próximo, mesmo em contas com grande volume de interações.",
  },
  {
    question: "Qual a diferença para uma automação tradicional de DM?",
    answer:
      "Automação tradicional usa respostas pré-definidas para palavras-chave. O agente de IA da FabChat interpreta o contexto, mantém o histórico da conversa e adapta a resposta às regras do negócio, criando uma experiência muito mais próxima de um atendente real.",
  },
];

export default function InstagramAgenteIa() {
  const baseUrl = getBaseUrl();
  const breadcrumbSchema = buildBreadcrumbSchema(baseUrl, BREADCRUMB);
  const organizationSchema = buildOrganizationSchema(baseUrl);
  const softwareSchema = buildSoftwareApplicationSchema(baseUrl, DESCRIPTION);
  const webPageSchema = buildWebPageSchema(baseUrl, { path: "/instagram/agente-ia", title: TITLE, description: DESCRIPTION });
  const faqSchema = buildFAQPageSchema(FAQ_ITEMS);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SeoHead title={TITLE} description={DESCRIPTION} canonicalPath="/instagram/agente-ia" />
      <StructuredData data={[organizationSchema, softwareSchema, breadcrumbSchema, webPageSchema, faqSchema]} />
      <Header />

      <main className="flex-1">
        <section className="container mx-auto px-4 py-16 md:py-24 max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Agente de IA para Instagram</h1>
          <p className="text-lg text-muted-foreground mb-12">
            Agente de IA para Instagram com foco em Direct e comentários: personalidade, regras e contexto configuráveis para atendimento e pré-venda no mesmo fluxo.
          </p>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">O que é o agente de IA no Instagram</h2>
            <p className="text-muted-foreground mb-4">
              Na FabChat, o agente de IA é um <strong className="text-foreground">assistente configurável</strong> conectado ao seu Instagram: responde a DMs e comentários com base em <strong className="text-foreground">personalidade, regras e comportamento</strong> que você define. Você controla tom de voz, quando encaminhar para humano e como tratar perguntas frequentes; a IA mantém o <strong className="text-foreground">contexto da conversa</strong>. A diferença em relação a “só automação de direct” é que o agente tem identidade e regras de negócio — não apenas respostas fixas. Tudo no mesmo painel do <Link to="/whatsapp/agente-ia" className="text-primary hover:underline">WhatsApp</Link>, com leads no <Link to="/integracoes/crm" className="text-primary hover:underline">CRM integrado</Link>.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">O que o agente entrega no Instagram para atendimento e vendas</h2>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex gap-3">
                <Bot className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span>Personalidade e regras configuráveis: comportamento alinhado ao seu playbook.</span>
              </li>
              <li className="flex gap-3">
                <MessageCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span>Contexto mantido pela IA em toda a conversa (DM e comentários).</span>
              </li>
              <li className="flex gap-3">
                <Instagram className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span>Respostas em direct e gestão de comentários; encaminhamento para humano quando fizer sentido.</span>
              </li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">Para quem faz sentido</h2>
            <p className="text-muted-foreground mb-4">
              Negócios que usam o Instagram para vendas ou atendimento e querem escalar o primeiro contato com um agente que tenha identidade e regras — não apenas “resposta automática”. O agente opera no perfil conectado; as conversas viram leads e histórico no mesmo painel do WhatsApp.
            </p>
          </section>

          <p className="text-sm text-muted-foreground mb-6">
            Quem busca especificamente <Link to="/instagram/automacao-dm" className="text-primary hover:underline">automação de DM e comentários</Link> encontra isso no mesmo agente; no WhatsApp temos o <Link to="/whatsapp/agente-ia" className="text-primary hover:underline">agente de IA para WhatsApp</Link>.
          </p>

          <section className="flex flex-col sm:flex-row gap-4 pt-6">
            <Button asChild size="lg" className="gap-2">
              <Link to="/planos">Ver planos <ArrowRight className="h-4 w-4" /></Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/contato">Falar com a gente</Link>
            </Button>
          </section>
        </section>
      </main>

      <Footer />
    </div>
  );
}
